import React, { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import Tooltip from 'carbon-react/lib/components/tooltip';
import Icon from 'carbon-react/lib/components/icon';
import Dialog from 'carbon-react/lib/components/dialog';
import Form from 'carbon-react/lib/components/form';
import Typography from 'carbon-react/lib/components/typography';
import Button from 'carbon-react/lib/components/button';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { UILoader } from '@futrli/ui-components';

import { deleteImportSheet } from '../../../../../../../core/api/external-sheets';
import { TRACKING_EVENTS } from '../../../../../../../core/constants';
import { trackEvent } from '../../../../../../../core/tracker';
import styles from './delete-button.scss';
import { usePredictionsInfoContext } from '../../../../../context/predictions-info-context';

interface ConfirmModalProps {
  isLoading: boolean;
  onClose: () => void;
  onDelete: () => void;
}

interface DeleteButtonProps {
  title?: string;
}

export const DeleteButton: FC<DeleteButtonProps> = props => {
  const intl = useIntl();
  const { fetchForecast, fetchForecastReport } = usePredictionsInfoContext();
  const { organisationId: tempOrganisationId, workspaceId: tempWorkspaceId, forecastId } = useParams();
  const organisationId = tempOrganisationId as string;
  const workspaceId = tempWorkspaceId as string;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const onDelete = async () => {
    setIsLoading(true);

    try {
      await deleteImportSheet(workspaceId, organisationId, forecastId as string);
    } catch {
      setIsLoading(false);
      setIsModalOpen(false);
      return;
    }

    trackEvent(TRACKING_EVENTS.IMPORT_DELETE);

    // update report and forecast to remove existing import sheet
    fetchForecastReport && fetchForecastReport('all');
    fetchForecast && (await fetchForecast());

    setIsLoading(false);
    setIsModalOpen(false);
  };

  return (
    <React.Fragment>
      {isModalOpen && <ConfirmDeleteModal isLoading={isLoading} onClose={closeModal} onDelete={onDelete} />}
      <Tooltip
        message={
          <FormattedMessage
            id="import.delete_tooltip"
            defaultMessage="This removes the link between your Master Google Sheet and Predict. This will reset the hot-link system if you want to use it again."
          />
        }
        position="top"
      >
        <div className={classNames(styles.buttonWrapper, styles.iconButtonContainer)}>
          <Button
            data-testid="delete-button"
            onClick={openModal}
            aria-label={intl.formatMessage({
              id: 'predict.delete_button.aria_label',
              defaultMessage: 'Delete button'
            })}
            buttonType="tertiary"
            p="8px"
          >
            {props.title ? (
              <p className={styles.buttonTitle}>{props.title}</p>
            ) : (
              <Icon type="delete" bg="#fff" bgSize="large" />
            )}
          </Button>
        </div>
      </Tooltip>
    </React.Fragment>
  );
};

const ConfirmDeleteModal: FC<ConfirmModalProps> = ({ isLoading, onClose, onDelete }: ConfirmModalProps) => {
  const intl = useIntl();

  return (
    <Dialog
      open={true}
      onCancel={onClose}
      size="medium-small"
      data-testid="import-dialog"
      aria-label={intl.formatMessage({ id: 'import.modal_title', defaultMessage: 'Disconnect master file' })}
      title={intl.formatMessage({ id: 'import.modal_title', defaultMessage: 'Disconnect master file' })}
    >
      <Form
        stickyFooter
        leftSideButtons={
          <Button disabled={isLoading} onClick={onClose} data-testid="import-delete-confirm-cancel">
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
        }
        saveButton={
          <Button
            disabled={isLoading}
            onClick={onDelete}
            buttonType="primary"
            data-testid="import-delete-confirm-continue"
          >
            <FormattedMessage id="common.continue" defaultMessage="Continue" />
          </Button>
        }
        onSubmit={onDelete}
      >
        <div>
          {isLoading ? (
            <UILoader />
          ) : (
            <Typography fontSize="16px" mb={0}>
              <FormattedMessage
                id="import.delete_confirm"
                defaultMessage={`
                We will remove the link between your current master file and Predict.
                You will be able to create a new master file after this. Note that if you need the file
                to be owned by someone else, they should be the one to create the new file."
              `}
              />
            </Typography>
          )}
        </div>
      </Form>
    </Dialog>
  );
};
